<script>
  import Button from '@leaflink/stash/Button.vue';
  import Dropdown from '@leaflink/stash/Dropdown.vue';
  import Loading from '@leaflink/stash/Loading.vue';
  import isEmpty from 'lodash-es/isEmpty';
  import { storeToRefs } from 'pinia';
  import { mapActions, mapGetters, mapState } from 'vuex';

  import { SPLIT_NAMES } from '@/constants/featureFlags';
  import { useCompanyStore } from '@/piniaStores/company/company.store';
  import { useShopStore } from '@/piniaStores/shop/shop.store';
  import { useUserStore } from '@/piniaStores/user/user.store';
  import { money } from '@/utils/i18n';

  import MiniCartStore from './components/MiniCartStore';
  import MiniCartToggle from './components/MiniCartToggle';

  export default {
    components: {
      Button,
      Dropdown,
      MiniCartStore,
      MiniCartToggle,
      Loading,
    },

    props: {
      companySlug: {
        type: String,
        required: true,
      },
      isCompanyBuyer: {
        type: Boolean,
        default: false,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
    },

    computed: {
      ...mapState({
        isLoading: (state) => state.cart.isLoading,
        isCartLoaded: (state) => state.cart.isLoaded,
      }),

      ...mapGetters({
        organizedItems: 'cart/organizedItems',
        items: 'cart/items',
        getCompanyById: 'cart/companyById',
      }),

      isLoaded() {
        return (
          this.activeCompany &&
          !isEmpty(this.getCompanyById(this.activeCompany.id)) && // MiniCartStore requires cart/companyById to never return undefined or else router-link throws an error
          this.isCartLoaded
        );
      },

      isEmpty() {
        return !this.items.length;
      },

      total() {
        // This assumes all item totals are of the same currency
        const total = this.items.reduce((acc, item) => {
          return acc + item.total.amount;
        }, 0);

        return {
          amount: total,
          currency: this.items[0].subtotal.currency,
        };
      },
    },

    setup() {
      const shopStore = useShopStore();

      const { fetchActiveCampaign } = shopStore;

      const { activeCampaign, getIsCampaignLive } = storeToRefs(shopStore);

      const companyStore = useCompanyStore();
      const { activeCompany } = storeToRefs(companyStore);

      const { getIsSplitEnabled } = storeToRefs(useUserStore());

      return {
        activeCampaign,
        fetchActiveCampaign,
        getIsCampaignLive,
        activeCompany,
        getIsSplitEnabled,
        SPLIT_NAMES,
      };
    },

    watch: {
      // Only check for an active campaign if there are items in the cart
      isEmpty(isEmpty) {
        if (isEmpty) {
          return;
        }

        this.fetchActiveCampaign();
      },
    },

    created() {
      this.fetchMiniCart();
    },

    mounted() {
      this.setupEventListeners();
    },

    beforeUnmount() {
      this.destroyEventListeners();
    },

    methods: {
      ...mapActions({
        fetchMiniCart: 'cart/fetchMiniCart',
        fetchCartCount: 'cart/fetchCartCount',
      }),

      money,

      // Listens for update cart events outside of Vue
      setupEventListeners() {
        document.addEventListener('cartItemCount', this.fetchCartCount);
      },

      // Destroys listeners as a clean-up step
      destroyEventListeners() {
        document.removeEventListener('cartItemCount', this.fetchCartCount);
      },
    },
  };
</script>

<template>
  <Dropdown
    close-manually
    data-test="dropdown|minicart"
    :class="{ [$style.dropdownEmpty]: isEmpty || !isLoaded }"
    :content-class="$style.content"
    :reattach="false"
  >
    <template #toggle="{ isActive, toggle }">
      <MiniCartToggle :disabled="disabled" :is-active="isActive" :show="toggle" />
    </template>
    <template #default="{ dismiss }">
      <div :class="$style.cart">
        <Loading v-if="!isLoaded" />
        <div v-else-if="!items.length" class="flex align-center align-middle h-full">
          <div class="text-center">
            <div class="ll-mb-2 heading-4">
              {{ $t('mini_cart.empty_cart') }}
            </div>
            <Button
              v-if="isCompanyBuyer"
              class="ll-mx-2 button"
              :href="getIsSplitEnabled(SPLIT_NAMES.SSR_IFRAME) ? undefined : `/c/${companySlug}/shop/brands/`"
              :to="getIsSplitEnabled(SPLIT_NAMES.SSR_IFRAME) ? `/c/${companySlug}/shop/brands/` : undefined"
              primary
            >
              {{ $t('common.shop_brands') }}
            </Button>
            <Button
              v-if="isCompanyBuyer"
              class="ll-mx-2 button"
              :href="getIsSplitEnabled(SPLIT_NAMES.SSR_IFRAME) ? undefined : `/c/${companySlug}/shop/`"
              :to="getIsSplitEnabled(SPLIT_NAMES.SSR_IFRAME) ? `/c/${companySlug}/shop/` : undefined"
              primary
            >
              {{ $t('common.shop_products') }}
            </Button>
          </div>
        </div>
        <template v-else>
          <transition-group name="mini-cart-list" tag="ul" :class="$style.stores">
            <MiniCartStore
              v-for="(store, index) in organizedItems"
              class="flex-1"
              :campaign-label="getIsCampaignLive ? activeCampaign.label : ''"
              :class="$style.item"
              :key="`key-${index}`"
              :store="store"
            />
          </transition-group>
          <div class="bg-white border-t flex ll-pa-2">
            <Button
              class="text-left flex align-middle align-center w-full"
              :href="getIsSplitEnabled(SPLIT_NAMES.SSR_IFRAME) ? undefined : '/cart'"
              :to="getIsSplitEnabled(SPLIT_NAMES.SSR_IFRAME) ? '/cart' : undefined"
              :class="$style.subtotalButton"
              primary
              @click="dismiss"
            >
              <span>
                {{ $t('mini_cart.proceed_to_cart') }}
              </span>
              <span class="flex-1 text-small text-right font-weight-regular">
                {{ $t('common.total') }}: {{ money(total) }} (+ {{ $t('common.shipping').toLowerCase() }})
              </span>
            </Button>
          </div>
        </template>
      </div>
    </template>
  </Dropdown>
</template>

<style lang="scss" module>
  .cart {
    background: var(--color-white);
    border-radius: $border-radius;
    display: flex;
    flex-direction: column;
    height: 490px;
    overflow: hidden;
    width: 400px;
  }

  .stores {
    height: 100%;
    list-style: none;
    margin: 0;
    overflow: auto;
    padding: 0;
  }

  .subtotalButton {
    display: flex;
  }

  .item {
    transition: $primary_transition;
  }

  .content {
    max-width: none !important;
    padding: 0;

    &::after {
      background: var(--color-ice-200) !important;
      left: auto;
      right: 29px !important;
    }
  }

  .dropdownEmpty .content::after {
    background: var(--color-white) !important;
  }
</style>
